// What the reader told us about their past, shown back beside the offer.
// Their own confirmed years are the most persuasive evidence we have, and
// they are theirs: we only repeat what they answered, misses included.

const LABELS = {
  marriage: "Partnership",
  childbirth: "Children",
  career: "A step in work",
  separation: "An ending in a relationship",
  difficult: "A harder stretch",
};
const ORDER = ["marriage", "childbirth", "career", "separation", "difficult"];
const storageKey = (requestId) => `ayupath-past-matches-${requestId}`;

/** Kept for this tab only, so the block survives a refresh of the preview. */
export function saveMatches(requestId, answers, windows) {
  try {
    sessionStorage.setItem(storageKey(requestId), JSON.stringify({ answers, windows }));
  } catch (err) {
    // Private browsing or blocked storage: the preview works without it.
  }
}

export function loadMatches(requestId) {
  try {
    return JSON.parse(sessionStorage.getItem(storageKey(requestId)) || "null");
  } catch (err) {
    return null;
  }
}

function years(windows) {
  return (windows || [])
    .map((w) => {
      const from = w.start.slice(0, 4);
      const to = w.end.slice(0, 4);
      return from === to ? from : `${from}–${to}`;
    })
    .join(" and ");
}

/**
 * The rows to show, or null when there is nothing worth showing: no answers,
 * or fewer than half matched. A mostly-wrong list beside a buy button would
 * be selling against the reader's own experience.
 */
export function matchSummary(matches) {
  const answers = matches?.answers || {};
  // What they recognised in themselves; "sometimes" counts, and says so.
  const traitRows = (matches?.windows?.traits || [])
    .filter((t) => ["yes", "sometimes", "no"].includes(answers[t.id]))
    .map((t) => ({
      event: t.id,
      label: t.label,
      years: "",
      matched: answers[t.id] !== "no",
      sometimes: answers[t.id] === "sometimes",
    }));
  // "na" (never applied to the reader) is left out; "other_year" is a miss.
  const pastRows = ORDER.filter((event) => ["yes", "no", "other_year"].includes(answers[event])).map(
    (event) => ({
      event,
      label: LABELS[event],
      years: years(matches.windows?.[event]),
      matched: answers[event] === "yes",
      otherYears: answers[event] === "other_year",
    })
  );
  const rows = [...traitRows, ...pastRows];
  const matched = rows.filter((r) => r.matched).length;
  if (matched === 0 || matched * 2 < rows.length) return null;
  return { rows, matched, asked: rows.length };
}

export default function PastMatches({ matches }) {
  const summary = matchSummary(matches);
  if (!summary) return null;
  const { rows, matched, asked } = summary;

  return (
    <section className="rounded-2xl border border-orange-200 bg-white p-5 space-y-3">
      <div className="space-y-1">
        <p className="text-xs font-semibold uppercase tracking-wide text-orange-600">
          Your chart and your life
        </p>
        <h3 className="text-lg font-semibold text-gray-900">
          {matched === asked
            ? `All ${asked} things your chart picked out matched you`
            : `${matched} of the ${asked} things your chart picked out matched you`}
        </h3>
        <p className="text-sm text-gray-600">That's what you told us a moment ago.</p>
      </div>
      <ul className="space-y-2 text-sm">
        {rows.map((row) => (
          <li key={row.event} className="flex items-start gap-2">
            <span
              className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full text-xs font-bold ${
                row.matched ? "bg-orange-500 text-white" : "bg-gray-100 text-gray-400"
              }`}
              aria-hidden="true"
            >
              {row.matched ? "✓" : "–"}
            </span>
            <span className={row.matched ? "text-gray-900" : "text-gray-500"}>
              <strong className="font-medium">{row.label}</strong>
              {row.years ? `, ${row.years}` : ""}
              {row.otherYears ? <span className="text-xs"> (it came in other years)</span> : null}
              {row.sometimes ? <span className="text-xs"> (sometimes)</span> : null}
              <span className="sr-only">{row.matched ? ": matched" : ": did not match"}</span>
            </span>
          </li>
        ))}
      </ul>
      <p className="text-sm text-gray-700">
        Your future periods come from the same calculation, using your birth date, time and place.
      </p>
    </section>
  );
}
